import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import SmoothScroll from "@/components/SmoothScroll";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import VideoBlock from "@/components/VideoBlock";
import ChaptersSection from "@/components/ChaptersSection";
import PoemBlock from "@/components/PoemBlock";
import Timeline from "@/components/Timeline";
import TheLaw from "@/components/TheLaw";
import Marquee from "@/components/Marquee";
import Gallery from "@/components/Gallery";
import AudioPlayer from "@/components/AudioPlayer";
import SubstackCTA from "@/components/SubstackCTA";
import Dedication from "@/components/Dedication";
import MothersVoice from "@/components/MothersVoice";
import CandleTribute from "@/components/CandleTribute";
import GuestBook from "@/components/GuestBook";
import ShareBar from "@/components/ShareBar";
import CustomCursor from "@/components/CustomCursor";
import Loader from "@/components/Loader";
import MemorialEntry from "@/components/MemorialEntry";

function Memorial() {
    const [loaded, setLoaded] = useState(false);
    return (
        <SmoothScroll>
            <Loader onDone={() => setLoaded(true)} />
            <MemorialEntry />
            <div className="grain-overlay" aria-hidden="true" />
            <CustomCursor />
            <Nav />
            <main id="top" data-testid="memorial-main" className="relative w-full">
                <Hero />
                <MothersVoice />
                <ChaptersSection />
                <TheLaw />
                <Timeline />
                <Marquee />
                <Gallery />
                <AudioPlayer />
                <SubstackCTA />
                <Dedication />
                <CandleTribute />
                <GuestBook />
                <footer
                    data-testid="footer"
                    className="relative w-full bg-[color:var(--paper)] py-14 px-6 sm:px-12 lg:px-16 border-t border-neutral-300"
                >
                    <div className="max-w-[92rem] mx-auto flex flex-col gap-10">
                        {/* Share her story */}
                        <div className="grid grid-cols-12 gap-6 sm:gap-8 pb-10 border-b border-neutral-300">
                            <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                Share Danielle&rsquo;s story
                            </div>
                            <div className="col-span-12 sm:col-span-9">
                                <ShareBar
                                    variant="light"
                                    label="Every share is a candle passed on"
                                />
                            </div>
                        </div>

                        {/* Follow Rebecca */}
                        <div className="grid grid-cols-12 gap-6 sm:gap-8 pb-10 border-b border-neutral-300">
                            <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                Follow Rebecca
                            </div>
                            <div className="col-span-12 sm:col-span-9">
                                <p className="font-serif italic text-lg sm:text-xl text-neutral-800 leading-relaxed max-w-2xl">
                                    Danielle&rsquo;s story is being told, in full, in these places. Follow, share, and keep her name alive.
                                </p>
                                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 font-sans text-sm">
                                    <a
                                        href="https://rebeccacharles.substack.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-substack-link"
                                        className="group inline-flex items-center gap-3 text-neutral-900 hover:text-neutral-500 transition-colors"
                                    >
                                        <span className="w-8 h-8 border border-neutral-400 flex items-center justify-center font-serif italic text-base group-hover:border-neutral-900 transition-colors">
                                            S
                                        </span>
                                        <span className="flex flex-col">
                                            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Substack</span>
                                            <span className="tabular">@rebeccacharles</span>
                                        </span>
                                    </a>
                                    <a
                                        href="https://twitter.com/rcharles109"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-twitter-link"
                                        className="group inline-flex items-center gap-3 text-neutral-900 hover:text-neutral-500 transition-colors"
                                    >
                                        <span className="w-8 h-8 border border-neutral-400 flex items-center justify-center font-serif italic text-base group-hover:border-neutral-900 transition-colors">
                                            X
                                        </span>
                                        <span className="flex flex-col">
                                            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Twitter / X</span>
                                            <span className="tabular">@rcharles109</span>
                                        </span>
                                    </a>
                                    <a
                                        href="https://www.facebook.com/rebecca.charles"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-facebook-link"
                                        className="group inline-flex items-center gap-3 text-neutral-900 hover:text-neutral-500 transition-colors"
                                    >
                                        <span className="w-8 h-8 border border-neutral-400 flex items-center justify-center font-serif italic text-base group-hover:border-neutral-900 transition-colors">
                                            f
                                        </span>
                                        <span className="flex flex-col">
                                            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Facebook</span>
                                            <span className="tabular">Rebecca Charles · Becky Charles</span>
                                        </span>
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                            <div>
                                <div className="text-neutral-900 mb-2">Justice for Sweet Danielle · Her Last 41 Days on This Earth</div>
                                <div>A Memorial for Danielle Cathleen Alvarez · Aug 5, 1993 — Oct 6, 2021 · Kept by her mother, Rebecca Charles Jackson.</div>
                            </div>
                            <div className="tabular">© {new Date().getFullYear()} — For Danielle.</div>
                        </div>

                        {/* Source note — cited across every evidence page */}
                        <div
                            data-testid="footer-source-note"
                            className="pt-8 mt-4 border-t border-neutral-300 font-serif italic text-xs sm:text-sm text-neutral-600 leading-relaxed max-w-4xl normal-case tracking-normal"
                        >
                            Every fact on this page is drawn from Danielle&rsquo;s certified 6,132-page hospital record, an independent autopsy, independent toxicology, or filings in the Supreme Court of the State of New York, County of Nassau, Index No. 605957/2024. Source pages available on request.
                        </div>
                    </div>
                </footer>
            </main>
        </SmoothScroll>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Memorial />} />
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
